import React, { useEffect, useState } from "react";
import UOMHttpService from "../../../services/http/products/uom.service";
import categoryHttpService from "../../../services/http/products/category.service";
import brandHttpService from "../../../services/http/products/brand.service";
import UnlimitInput from "../../../components/ui-kit/unlimit-input";
import UnlimitTextarea from "../../../components/ui-kit/unlimit-textarea";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import UnlimitSelect from "../../../components/ui-kit/unlimit-select";
import UnlimitSubmitButton from "../../../components/ui-kit/unlimit-submit-button";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";
import UnlimitToggle from "../../../components/ui-kit/unlimit-toggle";
import { useTranslation } from "react-i18next";

const ProductStep1 = ({
  handleNext,
  formData,
  setFormData,
  existingData,
  isLoading,
}) => {
  const { id } = useParams();
  const {
    register,
    handleSubmit,
    formState,
    setValue,
    getValues,
    control,
    watch,
  } = useForm();
  const { errors } = formState;
  const [brand, setBrand] = useState([]);
  const [UOMCode, setUOMCode] = useState([]);
  const [category, setCategory] = useState([]);
  const [brandLoading, setBrandLoading] = useState(false);
  const [UOMLoading, setUOMLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const { t } = useTranslation();

  // Fetch required data for dropdowns
  async function fetchCategory() {
    try {
      setCategoryLoading(true);
      const category = await categoryHttpService.categoriesList({
        is_active: 1,
      });
      setCategory(category?.data?.data?.data);
    } catch (err) {
      console.log("Error fetching categories:", err);
    } finally {
      setCategoryLoading(false);
    }
  }

  async function fetchBrand() {
    try {
      setBrandLoading(true);
      const brand = await brandHttpService.brandsList({ is_active: 1 });
      setBrand(brand?.data?.data?.data);
    } catch (err) {
      console.log("Error fetching brands:", err);
    } finally {
      setBrandLoading(false);
    }
  }

  async function fetchUOMCode() {
    try {
      setUOMLoading(true);
      const uom = await UOMHttpService.uomList({ is_active: 1 });
      setUOMCode(uom?.data?.data?.data);
    } catch (err) {
      console.log("Error fetching UOM codes:", err);
    } finally {
      setUOMLoading(false);
    }
  }

  useEffect(() => {
    fetchBrand();
    fetchUOMCode();
    fetchCategory();
  }, [id]);

  useEffect(() => {
    if (existingData) hydratePredefinedValues(existingData);
  }, [existingData]);

  // Hydrate form fields with predefined values
  const hydratePredefinedValues = (data) => {
    setValue("name", data.name);
    setValue("ean", data.ean);
    setValue("lm_product_number", data.lm_product_number);
    setValue("receipt_label_text", data.receipt_label_text);
    setValue("sku", data.sku);
    setValue("net_weight", data.net_weight);
    //setValue("weight_buffer", data.weight_buffer);
    setValue("actual_zemic_sensor_weight", data.actual_zemic_sensor_weight);

    if (data.category) {
      setValue("category_id", {
        ...data.category,
        label: data.category?.name,
        value: data.category?.id,
      });
    }

    if (data.uom_code_1) {
      setValue("uom_code_1_id", {
        ...data.uom_code_1,
        label: data.uom_code_1?.code,
        value: data.uom_code_1?.id,
      });
    }

    setValue("uom_code_1_value", data.uom_code_1_value);

    if (data.uom_code_2) {
      setValue("uom_code_2_id", {
        ...data.uom_code_2,
        label: data.uom_code_2?.name,
        value: data.uom_code_2?.id,
      });
    }

    setValue("uom_code_2_value", data.uom_code_2_value);

    if (data.brand) {
      setValue("brand_id", {
        ...data.brand,
        label: data.brand?.name,
        value: data.brand?.id,
      });
    }

    setValue("extra_security_check", data.extra_security_check == 1);
    setValue("short_description", data.short_description);
    setValue("product_information", data.product_information);
    setValue("is_security_product", data.is_security_product == 1);
    setValue("is_compound_product", data.is_compound_product == 1);
    setValue("is_active", data.is_active == 1);
  };

  // Handle form submission
  const onSubmitForm = async (data) => {
    const newFormData = new FormData();
    newFormData.append("ean", data.ean);
    newFormData.append("name", data.name);
    newFormData.append("lm_product_number", data.lm_product_number);
    newFormData.append("receipt_label_text", data.receipt_label_text);
    newFormData.append("brand_id", data.brand_id?.id);
    newFormData.append("category_id", data.category_id?.id);
    newFormData.append("sku", data.sku);
    newFormData.append("uom_code_1_id", data.uom_code_1_id?.id);
    newFormData.append("uom_code_1_value", data.uom_code_1_value);
    newFormData.append("short_description", data.short_description);
    newFormData.append("product_information", data.product_information);
    // newFormData.append(
    //   "extra_security_check",
    //   data.extra_security_check ? 1 : 0
    // );
    newFormData.append("net_weight", data.net_weight);
    newFormData.append(
      "actual_zemic_sensor_weight",
      data.actual_zemic_sensor_weight
    );
    // newFormData.append("is_security_product", data.is_security_product ? 1 : 0);
    // newFormData.append("is_compound_product", data.is_compound_product ? 1 : 0);
    // newFormData.append("uom_code_2_id", data.uom_code_2_id?.id || null);
    // newFormData.append("uom_code_2_value", data.uom_code_2_value || null);
    newFormData.append("is_active", data.is_active ? 1 : 0);

    setFormData(newFormData);
    handleNext();
  };

  const productName = watch("name");
  const uomCode2Selected = watch("uom_code_2_id");

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <div className="card">
        {isLoading ? (
          <div className="row d-flex justify-content-center">
            <UnlimitLoading width={50} height={50} />
          </div>
        ) : (
          <div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-3">
                  <UnlimitInput
                    label={t("inputField.placeholder.enterEAN")}
                    id="ean"
                    type="text"
                    placeholder={t("inputField.placeholder.enterEAN")}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                      pattern: {
                        value: /^[0-9]{4,14}$/,
                        message: t("inputField.validation.eanPattern"),
                      },
                    }}
                    error={errors.ean}
                    errorMessage={errors.ean?.message}
                  />
                </div>
                <div className="col-md-3">
                  <UnlimitInput
                    label={t("inputField.label.enterProductName")}
                    id="name"
                    type="text"
                    placeholder={t("inputField.placeholder.enterProductName")}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                      maxLength: {
                        value: 100,
                        message: t("inputField.validation.maxLength", {
                          value: 100,
                        }),
                      },
                    }}
                    error={errors.name}
                    errorMessage={errors.name?.message}
                  />
                </div>
                <div className="col-md-3">
                  <UnlimitInput
                    label={t("inputField.label.receiptLabelText")}
                    id="receipt_label_text"
                    type="text"
                    placeholder={t(
                      "inputField.placeholder.enterReceiptLabelText"
                    )}
                    register={register}
                    rules={{
                      maxLength: {
                        value: 100,
                        message: t("inputField.validation.maxLength", {
                          value: 100,
                        }),
                      },
                    }}
                    error={errors.receipt_label_text}
                    errorMessage={errors.receipt_label_text?.message}
                  />
                </div>
                <div className="col-lg-2 d-flex flex-column">
                  <UnlimitToggle
                    isChecked={id ? false : true}
                    label={t("inputField.label.isActive")}
                    id="is_active"
                    register={register}
                    column={true}
                  />
                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <UnlimitTextarea
                      label={t("inputField.label.productInformation")}
                      id="product_information"
                      register={register}
                      rules={{
                        required: t("inputField.validation.required"),
                        maxLength: {
                          value: 2000,
                          message: t("inputField.validation.maxLength", {
                            value: 2000,
                          }),
                        },
                      }}
                      numOfRows={3}
                      error={errors.product_information}
                      errorMessage={errors.product_information?.message}
                    />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="mb-3">
                    <UnlimitTextarea
                      label={t("inputField.label.shortDescription")}
                      id="short_description"
                      register={register}
                      rules={{
                        maxLength: {
                          value: 2000,
                          message: t("inputField.validation.maxLength", {
                            value: 2000,
                          }),
                        },
                      }}
                      numOfRows={3}
                      error={errors.short_description}
                      errorMessage={errors.short_description?.message}
                    />
                  </div>
                </div>
              </div>
            </div>
            <hr className="m-0" />
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <UnlimitInput
                    label={t("tableColumnName.lmProductNo")}
                    id="lm_product_number"
                    placeholder={t("inputField.placeholder.enterLMProductNo")}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                      maxLength: {
                        value: 18,
                        message: t("inputField.validation.maxLength", {
                          value: 18,
                        }),
                      },
                    }}
                    error={errors.lm_product_number}
                    errorMessage={errors.lm_product_number?.message}
                  />
                </div>
                <div className="col-md-3">
                  <UnlimitInput
                    label={t("tableColumnName.sku")}
                    id="sku"
                    placeholder={t("inputField.placeholder.enterSKU")}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                      maxLength: {
                        value: 18,
                        message: t("inputField.validation.maxLength", {
                          value: 18,
                        }),
                      },
                      minLength: {
                        value: 6,
                        message: t("inputField.validation.minLength", {
                          value: 6,
                        }),
                      },
                    }}
                    error={errors.sku}
                    errorMessage={errors.sku?.message}
                  />
                </div>
                <div className="col-md-3">
                  <UnlimitInput
                    label={t("inputField.label.weight")}
                    id="net_weight"
                    placeholder={t("inputField.placeholder.enterWeight")}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                      min: {
                        value: 0,
                        message: t("inputField.validation.minValue", {
                          min: 0,
                        }),
                      },
                    }}
                    error={errors.net_weight}
                    errorMessage={errors.net_weight?.message}
                  />
                </div>
                <div className="col-md-3">
                  <UnlimitInput
                    label={t("inputField.label.actualWeightByZemicSensor")}
                    id="actual_zemic_sensor_weight"
                    placeholder={t(
                      "inputField.placeholder.enterActualWeightByZemicSensor"
                    )}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                      min: {
                        value: 0,
                        message: t("inputField.validation.minValue", {
                          min: 0,
                        }),
                      },
                    }}
                    error={errors.actual_zemic_sensor_weight}
                    errorMessage={errors.actual_zemic_sensor_weight?.message}
                  />
                </div>

                <div className="col-md-3 mt-2">
                  <UnlimitSelect
                    label={t("inputField.label.brand")}
                    id="brand_id"
                    errors={errors}
                    control={control}
                    isLoading={brandLoading}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                    }}
                    placeholder={t("inputField.placeholder.chooseBrand")}
                    options={brand}
                    optionLabel="name"
                    optionValue="id"
                  />
                </div>
                <div className="col-md-3 mt-2">
                  <UnlimitSelect
                    label={t("inputField.label.productCategory")}
                    id="category_id"
                    errors={errors}
                    control={control}
                    isLoading={categoryLoading}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                    }}
                    placeholder={t("inputField.placeholder.chooseCategory")}
                    options={category}
                    optionLabel="name"
                    optionValue="id"
                  />
                </div>
                <div className="col-md-3 mt-2">
                  <UnlimitSelect
                    label={t("inputField.label.uomCode")}
                    id="uom_code_1_id"
                    errors={errors}
                    control={control}
                    isLoading={UOMLoading}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                    }}
                    placeholder={t("inputField.placeholder.chooseUOMCode")}
                    options={UOMCode}
                    optionLabel="code"
                    optionValue="id"
                  />
                </div>
                <div className="col-md-3 mt-2">
                  <UnlimitInput
                    label={t("inputField.label.uomValue")}
                    id="uom_code_1_value"
                    type="number"
                    step="0.01"
                    placeholder={t("inputField.placeholder.enterValue")}
                    register={register}
                    rules={{
                      required: t("inputField.validation.required"),
                      min: {
                        value: 0,
                        message: t("inputField.validation.minValue", {
                          min: 0,
                        }),
                      },
                    }}
                    error={errors.uom_code_1_value}
                    errorMessage={errors.uom_code_1_value?.message}
                  />
                </div>

                {/* <div className="col-md-3 mt-3">
                    <UnlimitSelect
                      label={t("inputField.label.uomCode2")}
                      id="uom_code_2_id"
                      errors={errors}
                      control={control}
                      isLoading={UOMLoading}
                      register={register}
                      placeholder={t("inputField.placeholder.select")}
                      options={UOMCode}
                      optionLabel="name"
                      optionValue="id"
                    />
                  </div>
                  <div className="col-md-3 mt-3">
                    <UnlimitInput
                      label={t("inputField.label.uomCode2Value")}
                      id="uom_code_2_value"
                      type="number"
                      step="0.01"
                      placeholder={t("inputField.placeholder.enterValue")}
                      register={register}
                      rules={{
                        required: uomCode2Selected
                          ? t("inputField.validation.required")
                          : false,
                        min: {
                          value: 0,
                          message: t("inputField.validation.minValue", {
                            min: 0,
                          }),
                        },
                      }}
                      error={errors.uom_code_2_value}
                      errorMessage={errors.uom_code_2_value?.message}
                    />
                  </div> */}

                {/*  <div className="col-md-3 mt-2">
                  <UnlimitToggle
                    label={t("inputField.label.isSecurityableProduct")}
                    id="is_security_product"
                    register={register}
                    column={true}
                  />
                </div>
                <div className="col-md-3 mt-2">
                  <UnlimitToggle
                    label={t("inputField.label.isCompoundProduct")}
                    id="is_compound_product"
                    register={register}
                    column={true}
                  />
                </div>
                <div className="col-md-3 mt-2">
                  <UnlimitToggle
                    label={t("inputField.label.extraSecurityCheck")}
                    id="extra_security_check"
                    register={register}
                    column={true}
                  />
                </div> */}
              </div>
            </div>
            <hr className="m-0" />
            <div className="card-body">
              <div className="col-lg-12 mb-3">
                <UnlimitSubmitButton
                  label={t("general.button.next")}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default ProductStep1;
